// Wave 26 — Google Business Profile (LocalBusiness) schema per Police Clearance province.
// Each province landing emits a fully-qualified LegalService/LocalBusiness node with
// geo coordinates, opening hours, service area and an OfferCatalog so Google Business
// Profile / Maps and AI crawlers can match the local entity to the page.

import { COMPANY } from "@/lib/company";
import type { PcProvinceLanding } from "@/content/police-clearance-provinces";

export interface PcProvinceGbp {
  lat: number;
  lng: number;
  postalCode: string;
  localityEn: string;
  streetEn: string;
  radiusMeters: number;
  priceFromThb: number;
  gbpNameSuffix: string;
}

export const PC_PROVINCE_GBP: Record<string, PcProvinceGbp> = {
  phuket: {
    lat: 7.8836,
    lng: 98.388,
    postalCode: "83000",
    localityEn: "Mueang Phuket",
    streetEn: "Chalermprakiat Rama 9 Rd., Talat Yai",
    radiusMeters: 45000,
    priceFromThb: 4900,
    gbpNameSuffix: "Phuket Town Head Office",
  },
  bangkok: {
    lat: 13.7455,
    lng: 100.5331,
    postalCode: "10330",
    localityEn: "Pathum Wan",
    streetEn: "Rama 1 Rd., Wang Mai",
    radiusMeters: 60000,
    priceFromThb: 3900,
    gbpNameSuffix: "Bangkok Partner Desk",
  },
  "chiang-mai": {
    lat: 18.7969,
    lng: 98.9673,
    postalCode: "50300",
    localityEn: "Mueang Chiang Mai",
    streetEn: "Nimmanhaemin Rd., Su Thep",
    radiusMeters: 50000,
    priceFromThb: 5200,
    gbpNameSuffix: "Chiang Mai Partner Desk",
  },
  "khon-kaen": {
    lat: 16.4322,
    lng: 102.8236,
    postalCode: "40000",
    localityEn: "Mueang Khon Kaen",
    streetEn: "Si Chan Rd., Nai Mueang",
    radiusMeters: 55000,
    priceFromThb: 5400,
    gbpNameSuffix: "Khon Kaen Partner Desk",
  },
  "udon-thani": {
    lat: 17.4103,
    lng: 102.7877,
    postalCode: "41000",
    localityEn: "Mueang Udon Thani",
    streetEn: "Prajak Silpakhom Rd., Mak Khaeng",
    radiusMeters: 55000,
    priceFromThb: 5400,
    gbpNameSuffix: "Udon Thani Partner Desk",
  },
};

export function getPcProvinceGbp(slug: string): PcProvinceGbp | undefined {
  return PC_PROVINCE_GBP[slug];
}

/**
 * LocalBusiness (LegalService) node for a Police Clearance province page.
 * lang="th" emits Thai names/descriptions; lang="en" the English mirror.
 */
export function buildPcProvinceLocalBusiness(
  p: PcProvinceLanding,
  site: string,
  lang: "th" | "en" = "th",
) {
  const g = getPcProvinceGbp(p.slug);
  const path =
    lang === "en"
      ? `${site}/en/police-clearance/province/${p.slug}`
      : `${site}/police-clearance/province/${p.slug}`;
  const name =
    lang === "en"
      ? `${COMPANY.brandName} — Police Clearance ${p.provinceEn}`
      : `${COMPANY.legalName} — ใบตรวจประวัติอาชญากรรม ${p.provinceTh}`;

  const offers = [
    {
      th: "ใบตรวจประวัติอาชญากรรม (PCSC) ภาษาไทย/อังกฤษ",
      en: "Thai Police Clearance Certificate (PCSC) — Thai/English",
    },
    { th: "พิมพ์ลายนิ้วมือ FD-258 (FBI/ACRO/AFP/RCMP)", en: "FD-258 fingerprinting (FBI/ACRO/AFP/RCMP)" },
    { th: "รับรองกงสุล MFA แจ้งวัฒนะ + Apostille 2026", en: "MFA legalization (Chaeng Watthana) + Apostille 2026" },
    { th: "แปลรับรองเอกสารพร้อมยื่นสถานทูต", en: "Certified translation + embassy submission" },
  ];

  return {
    "@context": "https://schema.org",
    "@type": ["LegalService", "LocalBusiness"],
    "@id": `${path}#localbusiness`,
    name,
    alternateName: g ? `${COMPANY.brandName} — ${g.gbpNameSuffix}` : COMPANY.brandName,
    description:
      lang === "en"
        ? `Police Clearance Certificate service in ${p.provinceEn}, Thailand — fingerprinting, PCSC/FBI/ACRO submission, MFA legalization and Apostille.`
        : p.aeoAnswer,
    url: path,
    telephone: COMPANY.phoneIntl,
    email: COMPANY.email,
    priceRange: "฿฿",
    currenciesAccepted: "THB",
    paymentAccepted: "Cash, Bank Transfer, PromptPay, Credit Card",
    knowsLanguage: ["th", "en"],
    address: {
      "@type": "PostalAddress",
      streetAddress: lang === "en" ? (g?.streetEn ?? p.rtpBranchAddress) : p.rtpBranchAddress,
      addressLocality: lang === "en" ? (g?.localityEn ?? p.provinceEn) : p.provinceTh,
      addressRegion: lang === "en" ? p.provinceEn : p.provinceTh,
      postalCode: g?.postalCode,
      addressCountry: "TH",
    },
    ...(g
      ? {
          geo: { "@type": "GeoCoordinates", latitude: g.lat, longitude: g.lng },
          hasMap: `https://www.google.com/maps/search/?api=1&query=${g.lat},${g.lng}`,
          serviceArea: {
            "@type": "GeoCircle",
            geoMidpoint: { "@type": "GeoCoordinates", latitude: g.lat, longitude: g.lng },
            geoRadius: g.radiusMeters,
          },
        }
      : {}),
    areaServed: [
      { "@type": "AdministrativeArea", name: p.provinceEn },
      { "@type": "Country", name: "Thailand" },
    ],
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ],
        opens: "08:00",
        closes: "20:00",
      },
    ],
    ...(g
      ? {
          makesOffer: {
            "@type": "Offer",
            priceSpecification: {
              "@type": "PriceSpecification",
              price: g.priceFromThb,
              priceCurrency: "THB",
              valueAddedTaxIncluded: true,
            },
          },
        }
      : {}),
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name:
        lang === "en"
          ? `Police Clearance services — ${p.provinceEn}`
          : `บริการใบตรวจประวัติอาชญากรรม — ${p.provinceTh}`,
      itemListElement: offers.map((o, i) => ({
        "@type": "Offer",
        position: i + 1,
        itemOffered: { "@type": "Service", name: lang === "en" ? o.en : o.th },
      })),
    },
    sameAs: COMPANY.websites,
  };
}